import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useMemo
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Avatar, Button, Checkbox, Tooltip } from 'antd';
import moment from 'moment';
import { toast } from 'react-toastify';
import { AssetItemDto } from '@api/Api';
import Loader from '@components/Loader';
import {
  restoreDeletedAssetAction,
  setFavoriteAssetAction
} from '@redux/actions/assetsAction';
import { formatDuration } from '@helpers/formatDuration';
import {
  getMediaDuration,
  getVideoScrubInfo,
  isAudioVideo
} from '@helpers/avInfo';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import AssetVersionsList from '@components/Assets/AssetVersionsList';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import Status from '@components/Approval/Status';
import { ActionToast } from '@components/Toasts';
import LottieComponent from '@components/Lottie';
import DueDate from '@components/Tasks/DueDate';
import { useCurrentTime } from '@hooks/useCurrentTime';
import { isDateSameOrBefore } from '@helpers/compareDueDate';
import { hashedColor } from '@helpers/hashedColor';
import UserAvatarLetters from '@helpers/userAvatarLetters';
import AssetActions from '@components/Tables/AssetActions';
import './AssetCard.less';
import { nonNull } from '@helpers/non-null';
import { CommentIcon, FavoriteIcon, RestoreAssetIcon } from '@icons';

const AssetCard = forwardRef(
  (
    {
      asset,
      selectedOne,
      canShare,
      isShowCompany = true,
      page
    }: {
      asset: AssetItemDto;
      selectedOne: boolean;
      canShare?: boolean;
      isShowCompany?: boolean;
      page: string;
    },
    ref: any
  ) => {
    const now = useCurrentTime();
    const dispatch = useDispatch();
    const { user } = useAuth(true, 'internal');
    const navigate = useNavigate();
    const location = useLocation();

    const scrubRef = useRef<any>();
    const scrubLineRef = useRef<any>();
    const assetRef = useRef<any>();
    const [scrubsVisible, setScrubsVisible] = useState<boolean>(false);
    const assetVersionId = asset.asset?.versions?.[0]?.id || '';

    const version = nonNull(asset.asset?.versions[0]);
    const { scrubsUrl, frameWidth, frameHeight, framesCount } =
      getVideoScrubInfo(version.metadata);
    const duration = isAudioVideo(version.metadata)
      ? getMediaDuration(version.metadata)
      : null;

    const currentStageDeadline = useMemo(() => {
      return asset.asset?.approvalWorkflow?.deadline;
    }, [asset.asset?.approvalWorkflow]);

    const isPastDueDate = useMemo(() => {
      if (!currentStageDeadline) return;
      return isDateSameOrBefore({
        dateToCompare: currentStageDeadline,
        baseDate: now
      });
    }, [now, currentStageDeadline]);

    const onMouseEnter = useCallback(() => {
      if (version.metadata.type !== 'video') return;
      setScrubsVisible(true);
      scrubLineRef.current.style.display = 'block';
    }, [setScrubsVisible, version]);

    const onMouseLeave = useCallback(() => {
      if (version.metadata.type !== 'video') return;
      setScrubsVisible(false);
      scrubLineRef.current.style.display = 'none';
    }, [setScrubsVisible, version]);

    const onMouseMove = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        const canvasEl = scrubRef.current;
        if (!canvasEl || !scrubsUrl) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const stepWidth = rect.width / framesCount;
        const frameIndex = Math.floor((e.clientX - rect.left) / stepWidth);
        const img = new Image();
        canvasEl.frameIndex = frameIndex;
        img.src = scrubsUrl;
        img.onload = () => {
          const canvasEl = scrubRef.current;
          if (!canvasEl || canvasEl.frameIndex !== frameIndex) return;

          if (scrubLineRef?.current) {
            scrubLineRef.current.style.left = `${Math.floor(
              e.clientX - rect.left
            )}px`;
          }

          const ctx = scrubRef.current.getContext('2d');

          ctx.drawImage(
            img,
            0,
            frameIndex * frameHeight,
            frameWidth,
            frameHeight,
            0,
            0,
            frameWidth,
            frameHeight
          );
        };
      },
      [scrubsUrl, frameWidth, frameHeight, scrubLineRef, framesCount, scrubRef]
    );

    const preparePreview = useMemo(() => {
      const res = assetPreviewThumbnails({
        url: version.metadata.preview?.url || '',
        status: version.metadata.preview?.status || '',
        type: version.metadata.type || '',
        format: 'img'
      });

      if (res === 'pending') return 'pending';
      if (typeof res === 'string')
        return <img draggable={false} src={res || ''} alt="" />;
      return res;
    }, [version]);

    return (
      <div
        ref={ref}
        className="assets_card_container"
        data-marquee-id={asset.id}
        data-marquee-type="asset"
      >
        <div
          className={classNames(
            'asset_card_wr',
            asset?.deletedAt && 'deleted_asset_card_wr'
          )}
          data-id={asset.id}
        >
          <Tooltip
            title={
              asset.isFavorite ? 'Remove from favorite' : 'Add to favorite'
            }
            placement="bottomRight"
            overlayClassName="toggle_favorite_overlay"
          >
            <div
              className={classNames(
                asset?.deletedAt && 'favorite_icon_deleted',
                asset?.isFavorite ? 'favorite' : 'non_favorite',
                'favorite_icon'
              )}
              onClick={async () => {
                if (asset.deletedAt) return;
                if (!asset?.isFavorite) {
                  dispatch(
                    setFavoriteAssetAction(
                      {
                        assetId: asset.id,
                        isFavorite: !asset.isFavorite
                      },
                      page === 'favorite'
                    )
                  );
                  MixpanelService.track(
                    user.id,
                    MixpanelEventType.MANAGE_ASSETS,
                    {
                      addToFavorite: true,
                      deleteAsset: false,
                      newAsset: false,
                      externalLinkShare: false,
                      approvalRouting: false,
                      newCommentAsset: false,
                      annotationLeft: false,
                      taskFromComment: false
                    }
                  );
                  return;
                }
                dispatch(
                  setFavoriteAssetAction(
                    {
                      assetId: asset.id,
                      isFavorite: !asset.isFavorite
                    },
                    page === 'favorite'
                  )
                );
                toast(
                  <ActionToast
                    title="Media has been removed from favorites"
                    onUndo={undefined}
                    description={undefined}
                    closeToast={undefined}
                  />,
                  {
                    hideProgressBar: true,
                    bodyClassName: 'toast_container--invitation'
                  }
                );
              }}
            >
              <FavoriteIcon />
            </div>
          </Tooltip>
          {isPastDueDate && (
            <div className="asset_duedate">
              <DueDate urgent={true} date={currentStageDeadline} />
            </div>
          )}
          <Checkbox
            className={classNames(selectedOne && 'show_checkbox')}
            value={asset?.id}
          />
          <div
            className={classNames('assets_card_container__preview', {
              opacity_card_container: !!asset?.deletedAt
            })}
            onClick={() => {
              if (!asset.deletedAt)
                navigate(`/media-viewer/${asset.id}/${assetVersionId}`, {
                  state: { returnTo: location.pathname + location.search }
                });
            }}
          >
            {asset?.deletedAt && (
              <div className="asset_card_deleted_hover">
                <div />
                <Button
                  onClick={() => {
                    dispatch(
                      restoreDeletedAssetAction({
                        assetId: asset.id
                      })
                    );
                  }}
                >
                  <RestoreAssetIcon />
                  <span>Restore</span>
                </Button>
              </div>
            )}
            <div ref={assetRef} className="asset-image-container">
              <div className="click_icon">
                <div className="click_icon--overlay" />
                <LottieComponent view="click" className="icon" />
              </div>
              {preparePreview === 'pending' && (
                <Loader
                  classNameWrapper="assets-loader-wrapper"
                  spinSize={40}
                  isSpinning={true}
                />
              )}
              <div
                className="image_block"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseMove={onMouseMove}
              >
                {preparePreview !== 'pending' && preparePreview}
                {scrubsVisible && (
                  <canvas
                    ref={scrubRef}
                    width={frameWidth}
                    height={frameHeight}
                    className="scrubs"
                  />
                )}
                <div ref={scrubLineRef} className="scrub_line" />
              </div>
            </div>
            <div className="preview_info">
              <div className="preview_info_left">
                <div style={asset?.deletedAt ? { visibility: 'hidden' } : {}}>
                  {!!asset.asset?.approvalWorkflow && (
                    <Status
                      showOverdueTag
                      status={asset.asset.approvalWorkflow.status}
                      approvalWorkflowStatus={
                        asset.asset.approvalWorkflow.status
                      }
                      approvalWorkflowIsPaused={
                        asset.asset.approvalWorkflow.isPaused
                      }
                      deadline={asset.asset.approvalWorkflow.deadline}
                    />
                  )}
                </div>
                <div>
                  <Tooltip
                    title={asset.owner?.user?.name}
                    placement="bottom"
                    overlayClassName="toggle_favorite_overlay"
                  >
                    <Avatar
                      src={asset.owner?.user?.picture.url}
                      size={24}
                      style={{
                        color: '#161819',
                        fontWeight: 'bold',
                        backgroundColor: hashedColor(
                          asset.owner?.user?.email ?? '',
                          'mediaViewerComment'
                        )
                      }}
                    >
                      {UserAvatarLetters(asset.owner?.user?.name)}
                    </Avatar>
                  </Tooltip>
                </div>
              </div>
              {!!duration && (
                <div className="preview_info_duration">
                  {formatDuration(duration)}
                </div>
              )}
            </div>
          </div>
          <div className="assets_card_container__content">
            <div className="assets_card_container__content_body">
              <div className="title">
                <Tooltip
                  placement="topLeft"
                  overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                  title={`${version.name}.${version.extension}`}
                >
                  <span
                    className="copy"
                    onClick={() => {
                      if (!asset.deletedAt)
                        navigate(
                          `/media-viewer/${asset.id}/${assetVersionId}`,
                          {
                            state: {
                              returnTo: location.pathname + location.search
                            }
                          }
                        );
                    }}
                  >
                    {`${version.name}.${version.extension}`}
                  </span>
                </Tooltip>
                <AssetVersionsList
                  versionsCount={asset?.asset?.versionsCount}
                  permissions={asset?.asset?.permissions}
                  versions={asset?.asset?.versions}
                  assetId={asset.id}
                />
              </div>
              <div className="info">
                <span className="info__date">
                  {moment(asset?.modifiedAt).format('MMM DD, YYYY')}
                </span>
                {asset.campaignId && asset.campaignName && isShowCompany && (
                  <div className="info__campaign">
                    <Link
                      to={`/campaigns/single/${asset.campaignId}`}
                      className="info__campaign-link"
                    >
                      {asset.campaignName}
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <div className="assets_card_container__content_action">
              <AssetActions asset={asset} canShare={canShare} page={page} />
              {(asset.asset?.versions[0].commentsCount || 0) > 0 &&
                !asset.deletedAt && (
                  <div className="info__comments">
                    <CommentIcon />
                    <span>{asset.asset?.versions[0].commentsCount}</span>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default AssetCard;
